import { useState } from "react";
import { ApolloError, useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { TRANSFER_EDUCATION_PROGRAM_MUTATION } from "../queries";
import { CREATE_EDUCATION_PROGRAM_LEVEL_REFETCH_QUERIES } from "@/features/person/components/PGMProgramTable/mutations";
import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import Banner from "@/components/Banner";
import { Person } from "@/types";
import { pgm31aTransferCopy, type Pgm31aTransferLevelCd } from "../_utils/pgm31aTransfer";

interface TransferModalProps {
  pgaId: string;
  personData: Person;
  levelCd: Pgm31aTransferLevelCd;
  onClosed: () => void;
}

export default function TransferModal({ pgaId, personData, levelCd, onClosed }: TransferModalProps) {
  const { Modal } = useModal();
  const copy = pgm31aTransferCopy(levelCd);

  const [transferEducationProgram, { loading: mutationLoading }] = useMutation(TRANSFER_EDUCATION_PROGRAM_MUTATION, {
    refetchQueries: CREATE_EDUCATION_PROGRAM_LEVEL_REFETCH_QUERIES,
    awaitRefetchQueries: true
  });
  const [bannerError, setBannerError] = useState("");

  const handleTransfer = async () => {
    try {
      const { data } = await transferEducationProgram({
        variables: {
          input: {
            pgaId,
            levelCd
          }
        }
      });

      const { success, message, code } = data?.transferEducationProgram ?? {};
      if (success) {
        if (code === "SYNC_FAILED") {
          showSnackbar(
            message ||
              "Requirements transferred in CCMS, but education sync failed. Re-sync this member if Canvas still shows the old portfolio.",
            { variant: "warning" }
          );
        } else {
          showSnackbar("Transferred to automated program", { close: true });
        }
        onClosed();
      } else {
        setBannerError(message || "The transfer could not be completed.");
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  };

  const action = (
    <Button loading={mutationLoading} onClick={handleTransfer}>
      Transfer
    </Button>
  );

  return (
    <Modal
      action={action}
      onClosed={onClosed}
      headline={copy.headline}
      data-testid="transfer-modal"
      hasFormElements={false}
      className="w-[490px]"
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The transfer could not be completed." subText={bannerError} />
        </div>
      )}
      <p>
        Are you sure you wish to transfer {personData.firstName} {personData.lastName} to {copy.destination}?
        {copy.keepClassicCourseworkNote} This cannot be undone and will take effect immediately.
      </p>
    </Modal>
  );
}
